/**
 * A MUNKALISTA — „mit kell feldolgoznom?"
 *
 * Három hasáb *(#68, a „B" elrendezés, Viktor 2026-10-19 —
 * `docs/tervezes/design/munkalista-papir-kozepen/`)*: balra a kupac, középen
 * a papír a fejlécével, jobbra a döntés — a jelöltek, a Tensoft-kontír
 * („Valódi kontír", #54), a Tételsorok, és alul a Lefűzöm.
 *
 * A LEFŰZÉS KÉT KIJELÖLÉS METSZETE: a bizonylat (bal oszlop) ÉS a hiánytétel,
 * amit lezár (jobb oszlop). Ez nem felületi kényelmetlenség, hanem a
 * szerkezet tükre: a feltöltött bizonylatnak nincs kiolvasott mezője
 * (2.6/19), tehát a rendszer NEM TUDJA megmondani, melyik hiányhoz tartozik.
 * A könyvelő tudja — és a rendszer ezt kérdezi meg tőle.
 *
 * A „KINYERT ADATOK" PANEL ELŐRE MUTAT, nem vissza. A mezők a NAV-bejelentés
 * mezői, és a lefűzés pillanatában íródnak rá a bizonylatra. A panel ezért
 * azt mondja, hogy „ezek a mezők kerülnek rá" — a múlt időbe tett felirat
 * azt hazudná, hogy már megtörtént a kinyerés.
 *
 * ═══ A SOR-MÓD MEGJELENÍTÉSE (2.6/22) ═══
 *
 * A SIKERES MENTÉSNEK NINCS ÜZENETE, és ez nem hiány. Optimista mentésnél a
 * visszajelzés maga a sor eltűnése és a kurzor továbblépése — egy „Lefűzve"
 * mondat naponta több százszor villanna fel, és pont a bukást tanítaná meg
 * elnézni. Az ÜZENETE A BUKÁSNAK VAN: az áll a lista tetején, amíg a
 * könyvelő nem intézi el.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CashAccountingMark } from '@/components/common';
import type { DiscrepancyListItem } from '@/features/discrepancies/types';
import type { DiscrepancyListState } from '@/features/discrepancies/useDiscrepancyList';
import {
  docTypeLabel,
  formatAmount,
  formatDate,
  formatMonthDay,
  formatTaxNumber,
} from '@/i18n/generated';
import { BookingSheet } from './bookingProposals/BookingSheet';
import {
  type BookingProposalsState,
  useBookingProposals,
} from './bookingProposals/useBookingProposals';
import { rankCandidates } from './candidateRanking';
import { columnKey, focusCandidates, focusNotes } from './columnKeys';
import { DocumentOriginal } from './DocumentOriginal';
import { CORRECTABLE_DOC_TYPES } from './docTypeChoice';
import { type PendingExclusion, secondsLeft } from './exclusion';
import { FieldCorrection } from './FieldCorrection';
import { groupByCompany, type QueueGroup } from './queueGroups';
import { TypeHintNotice } from './TypeHintNotice';
import { TensoftBooking } from './tensoft/TensoftBooking';
import type { TensoftBookingState } from './tensoft/useTensoftBooking';
import type { WorklistItem } from './types';
import { type DocumentFileState, useDocumentFile } from './useDocumentFile';
import { type FieldCorrectionState, useFieldCorrection } from './useFieldCorrection';
import { type PaperNotesState, usePaperNotes } from './usePaperNotes';
import type { FailedFiling, WorklistState } from './useWorklist';

interface WorklistProps {
  state: WorklistState;
  /**
   * A HIÁNYLISTA ÁLLAPOTA — innen jönnek a jelöltek.
   *
   * Nem külön lekérés: a hiánylista már be van töltve, és a jelöltek annak a
   * RÉSZHALMAZA. Egy második végpont ugyanazt az adatot hozná el másodszor,
   * és a két másolat elcsúszhatna — a könyvelő olyan tételt látna
   * jelöltként, ami a hiánylistán már nincs ott.
   */
  discrepancies: DiscrepancyListState;
  /** A kijelölt bizonylat Tensoft-állapota — az `App` tartja (rövidítések). */
  tensoft: TensoftBookingState;
}

/**
 * A SÁV A STÁTUSZ-ÁGAK FÖLÖTT ÁLL, nem alattuk — és ez nem elrendezés,
 * hanem zsákutca-elhárítás.
 *
 * A `WorklistBody` négy ágon KORÁN visszatér (`loading`, `failed`,
 * `forbidden`, üres kupac). Ha a sáv azok alatt renderelne, a 409 „Frissítem"
 * gombja `loading`-ba vinné a listát, és ha a frissítés is elbukik — ugyanaz
 * a kiesés, ami a mentést megfogta, tehát ÉPP ez a valószínű eset —, akkor a
 * sáv és vele az „Elvetem" kijárat ELTŰNNE, miközben a fül tiltva marad és a
 * `beforeunload` a lapfrissítést is fogja. Kiút nélküli állapot.
 * *(Code review fogta meg, 09-03.)*
 */
export function Worklist({ state, discrepancies, tensoft }: WorklistProps) {
  return (
    <>
      <FilingFailures state={state} discrepancies={discrepancies} />
      <Excluded state={state} />
      <WorklistBody state={state} discrepancies={discrepancies} tensoft={tensoft} />
    </>
  );
}

function WorklistBody({ state, discrepancies, tensoft }: WorklistProps) {
  const { t } = useTranslation();

  /**
   * A PAPÍR CÍME EGY HELYEN KÉRŐDIK EL — a `state.focusedId`-ből, MINDEN
   * korai visszatérés FÖLÖTT.
   *
   * A horog nem állhat a `loading`/`forbidden`/`failed`/üres ágak alá: azok
   * elágazó `return`-ök, egy alattuk álló `useDocumentFile` pedig a
   * renderek közt hol lefutna, hol nem — ez a horog-szabályok megsértése,
   * és Reactban nem stílushiba, hanem elcsúszó horog-sorrend.
   *
   * A KÖZÖS HÍVÁS a másik ok: az eredményt a jobb hasáb panelje kapja, és
   * ha máshol is elkérnénk, ugyanarra a papírra két aláírt cím készülne két
   * hálózati körrel. A `null` azonosítónál a horog nem kér semmit.
   */
  const fileState = useDocumentFile(state.focusedId);

  /**
   * A PUSKA IS IDE KERÜLT, a `Detail`-ből (#64). A jelölő a puska sora, és
   * két hasábban látszik: a papíron (jobbra) és a Tételsorokban (középen).
   * A közös szülő tartja, különben a kettő két külön másolatot töltene be,
   * és a papíron letett jelölő a Tételsorokban csak újratöltés után jelenne
   * meg. Ugyanazon okból áll a korai visszatérések fölött, mint a fájl.
   */
  const booking = useBookingProposals(state.focusedId);
  const notes = usePaperNotes(booking, state.focusedId);

  // A `→` BELÉP A BIZONYLATBA (#68): a fókusz a papírra kerül, a bordó keret
  // mutatja. Belépve a `Tab` a hasábok sorrendjében visz tovább — papír,
  // jelöltek, Tételsorok, Lefűzöm —, mert a DOM-sorrend is ez.
  //
  // ⚠️ CSAK AZ ÚJ KÉRÉSRE. A kérés az `App`-ban él, a munkalista viszont
  // felületváltáskor újraépül — a felépüléskor talált kérés a MÚLTÉ, nem
  // most nyomták le.
  const paper = notes.paper;
  const seenEnter = useRef(state.enterRequest);
  useEffect(() => {
    if (state.enterRequest !== null && state.enterRequest !== seenEnter.current) {
      seenEnter.current = state.enterRequest;
      paper.current?.focus();
    }
  }, [state.enterRequest, paper]);

  if (state.status === 'loading') {
    return <p className="hint">{t('worklist.loading')}</p>;
  }

  // A KÉT HIBAÁG KÜLÖN, mert a könyvelő mást tud kezdeni velük: a `forbidden`
  // zsákutca (emberhez kell fordulnia), a `failed` újrapróbálható.
  if (state.status === 'forbidden') {
    return <p className="error">{t('worklist.forbidden')}</p>;
  }

  if (state.status === 'failed') {
    return (
      <p className="error">
        {t('worklist.failed')}{' '}
        <button type="button" onClick={state.reload}>
          {t('worklist.retry')}
        </button>
      </p>
    );
  }

  const list = state.list;
  if (!list || list.totals.itemCount === 0) {
    return <p className="empty">{t('worklist.empty')}</p>;
  }

  const focused = list.items.find((item) => item.id === state.focusedId) ?? null;
  const failures = new Map(state.failed.map((entry) => [entry.documentId, entry]));

  /**
   * A CÉGEK CSAK AKKOR KAPNAK FEJLÉCET, HA VAN MIT MEGKÜLÖNBÖZTETNI (#68).
   *
   * Egyetlen cég bizonylatainál a fejléc csak egy sor ismétlés — nem
   * információ, hanem zaj, és 272px-en pont attól veszi el a helyet, ami
   * számít. A hovatartozás nem vész el: a bizonylat fejléce mindig kiírja.
   *
   * Az ADATBÓL dől el, nem a jogosultságból: az „iroda minden cége" hatókör
   * is állhat úgy, hogy ma egyetlen cégnek van elintézetlen papírja.
   *
   * DE CSAK AKKOR, HA A TELJES KUPACOT LÁTJUK. A szerver 200 sornál vág
   * (`WORKLIST_LIMIT`), a `totals.itemCount` viszont teljes darabszám —
   * a csonkolt lap NEM a sokaság. Ha a legrégebbi 200 papír történetesen
   * egyetlen cégé, a cég neve sehol nem állna a kupacban, miközben a
   * fejléc 250-et mond. Csonkolt listánál ezért kiírjuk: a fölösleges
   * fejléc olcsóbb hiba, mint a hiányzó.
   */
  const truncated = list.items.length < list.totals.itemCount;

  // A DÖNTÉS A KULCSRA SZÓL, NEM A BIZONYLATRA. A PontCo `112`-es esetében a
  // kulcson osztozó MÁSIK bizonylat is a listán állhat — egy döntés után az ő
  // jelvénye is lekerül (code review, 10-05).
  const decidedKeys = new Set(
    list.items
      .filter((item) => item.tensoftChoiceKey !== null && tensoft.decided.has(item.id))
      .map((item) => item.tensoftChoiceKey)
  );
  const showCompany = truncated || new Set(list.items.map((item) => item.companyId)).size > 1;

  return (
    <div className="worklist">
      <section className="worklist__queue" aria-label={t('worklist.unfiled')}>
        <h2>
          {t('worklist.unfiled')} <span className="count">{list.totals.itemCount}</span>
        </h2>

        <Search state={state} />

        {/* A KUPAC LISTA, NEM TÁBLÁZAT. A sor három adata nem három
            összehasonlítható oszlop, hanem EGY bizonylat két sorban: a
            partner neve, alatta a típus és a kelte, jobbra az összeg — így
            áll az artboardon, és 272px-en csak így fér el olvashatóan.
            A KIJELÖLÉS `aria-current`, nem `listbox`/`option`: ugyanaz a
            minta, mint a hiánylista sorain, és a `<ul>` így megtartja a
            saját lista-szerepét („6 elem") a képernyőolvasóban. */}
        {/* A LISTÁNAK NINCS KÜLÖN NEVE: a `<section>` már „Elintézetlen",
            és a fejléc a darabszámot is viszi — egy `aria-labelledby` innen
            „Elintézetlen 6"-ot mondatna a képernyőolvasóval, harmadszor.

            ÜGYFELENKÉNT CSOPORTOSÍTVA (#68), de csak ha van mit
            megkülönböztetni: egy cégnél a fejléc csak helyet venne el. A
            csoport fejléce a cégnevet a sorból is kiveszi — kétszer ugyanaz
            zaj volna. */}
        <div className="worklist__groups">
          {(showCompany ? groupByCompany(state.visible) : [null]).map((group) => (
            <div key={group?.companyId ?? 'all'} className="worklist__group">
              {group === null ? null : <QueueGroupHead group={group} />}
              <ul className="worklist__rows">
                {(group?.items ?? state.visible).map((item) => (
                  <QueueRow
                    key={item.id}
                    item={item}
                    needsChoice={
                      item.tensoftChoiceKey !== null && !decidedKeys.has(item.tensoftChoiceKey)
                    }
                    focused={item.id === state.focusedId}
                    failure={failures.get(item.id) ?? null}
                    state={state}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* NULLA TALÁLAT — ÉS A CSONKOLÁS KIMONDVA. A szerver 200 sornál vág;
            csonkolt listán a „nincs találat" NEM azt jelenti, hogy nincs
            ilyen bizonylat, csak azt, hogy a betöltött kétszázban nincs.
            Elhallgatva ez a 2.4d „hamis nulla" bukásmódja. */}
        {state.query.trim().length > 0 && state.visible.length === 0 ? (
          <p className="worklist__no-match">
            {t('worklist.search.noMatch')}
            {truncated ? ` ${t('worklist.search.truncated', { count: list.items.length })}` : ''}
          </p>
        ) : null}

        {/* AZ „ADATRA VÁR" TÁJÉKOZTATÁS, nem kapu. Csak az elakadt kinyerés
            számít bele — a frissen érkezett bizonylat nem vár adatra, a
            mezői a lefűzéskor íródnak rá. A szójegyzék szerint 24 óra után
            az üzemeltetéshez megy, nem az ügyfélhez. */}
        {list.totals.awaitingDataCount > 0 ? (
          <p className="worklist__waiting">
            {t('worklist.awaitingData', {
              count: list.totals.awaitingDataCount,
            })}
          </p>
        ) : null}

        {/* A KUPAC BILLENTYŰI (#68) — a négy, amivel a sor-mód jár. Itt
            tanítja a `→`/`←` párt, ami új; a teljes lista a súgóban (`?`). */}
        <p className="worklist__keys">
          <span>
            <kbd>↑↓</kbd> {t('worklist.keys.move')}
          </span>
          <span>
            <kbd>→</kbd> {t('worklist.keys.enter')}
          </span>
          <span>
            <kbd>←</kbd> {t('worklist.keys.back')}
          </span>
          <span>
            <kbd>Enter</kbd> {t('worklist.keys.file')}
          </span>
          {/* A `Tab` BÁRHOVA VISZ (Viktor, 10-20): a hasábok sorrendjében —
              papír, jelöltek, Tételsorok, Lefűzöm. Az eszközsor gombjai
              ezért nem `Tab`-állomások. */}
          <span>
            <kbd>Tab</kbd> {t('worklist.keys.next')}
          </span>
        </p>
      </section>

      {focused ? (
        <Detail
          item={focused}
          state={state}
          discrepancies={discrepancies}
          booking={booking}
          notes={notes}
          fileState={fileState}
          tensoft={tensoft}
        />
      ) : null}
    </div>
  );
}

/**
 * A KERESŐMEZŐ — a D9 `/`-e, „keresés az aktuális listában".
 *
 * ⚠️ MINDIG OTT VAN (#68, a „B" elrendezés, Viktor 2026-10-19). A 09-03-i
 * döntés még csak a `/`-re nyitotta, mert egy BEZÁRT, de aktív szűrő
 * láthatatlanul rejtett volna el sorokat. Állandó mezőnél ez a veszély
 * nincs: a szűrő szövege mindig a kupac tetején áll. Az ára egy sor hely és
 * egy `Tab`-állomás a 272 px-es hasábban.
 *
 * A `/` ide visz, az `Esc` törli a szűrőt, és visszavisz a kijelölt sorra.
 */
function Search({ state }: { state: WorklistState }) {
  const { t } = useTranslation();
  const field = useRef<HTMLInputElement>(null);

  // A `/` IDE VISZ — és kijelöli, ami benne áll: a következő leütés új
  // keresést kezd, nem a régi végére ír.
  //
  // ⚠️ CSAK AZ ÚJ KÉRÉSRE. A kérés az `App`-ban él, a mező viszont
  // felületváltáskor újraépül: a felépüléskor talált régi `/` a keresőbe
  // tenné a fókuszt, és onnan a `J`/`K`/`X` betűket gépelne.
  const seen = useRef(state.searchRequest);
  useEffect(() => {
    if (state.searchRequest !== null && state.searchRequest !== seen.current) {
      seen.current = state.searchRequest;
      field.current?.focus();
      field.current?.select();
    }
  }, [state.searchRequest]);

  return (
    <div className="worklist__search">
      <input
        ref={field}
        type="search"
        className="worklist__search-field"
        aria-label={t('worklist.search.label')}
        aria-keyshortcuts="/"
        placeholder={t('worklist.search.placeholder')}
        value={state.query}
        onChange={(event) => state.setQuery(event.target.value)}
        onKeyDown={(event) => {
          // AZ `Esc` HELYBEN ÉL. A globális `Escape` a súgót csukja; ha ez
          // kiszivárogna, egyetlen leütés kettőt csinálna.
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            state.leaveSearch();
          }
          // A `↓` A KERESŐBŐL A TALÁLATOKRA VISZ (Viktor, 10-20: „nem lehetett
          // lemenni a listába"). A szűrő MARAD — az `Esc`-től eltérően ez
          // nem kilépés, hanem továbbhaladás a szűkített kupacban.
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            state.focusQueue();
          }
        }}
      />
      <span className="worklist__search-key" aria-hidden="true">
        /
      </span>
    </div>
  );
}

/**
 * EGY CÉG CSOPORTJA a kupacban (#68): fejléc a cég nevével és a papírjai
 * számával, alatta a sorai. A csoportosítás a horogban történt
 * (`queueGroups.ts`) — itt csak a fejléc kerül a sorok elé.
 */
function QueueGroupHead({ group }: { group: QueueGroup }) {
  return (
    <h3 className="worklist__group-head">
      <span className="worklist__group-name">{group.companyName}</span>
      <span className="worklist__group-count">{group.items.length}</span>
    </h3>
  );
}

/**
 * A BUKOTT MENTÉSEK SÁVJA — a 2.6/22 harmadik pontja: „a bukás legyen
 * elmulaszthatatlan. Nem eltűnő buborék."
 *
 * `role="alert"`: a képernyőolvasó akkor is elmondja, ha a könyvelő közben
 * gépel — épp az a helyzet, amiben a bukás érkezni szokott.
 *
 * MINDEN SORNAK KÉT KIJÁRATA VAN, és ez nem díszítés. Ha csak az
 * „Újra" volna, egy tartósan bukó sor CSAPDÁBA ejtené a könyvelőt: a sáv nem
 * tűnne el, kilépni sem lehetne. Az „Elvetem" nem a mentést vonja vissza
 * (nincs mit) — azt mondja ki, hogy tudomásul vette.
 */
function FilingFailures({ state, discrepancies }: Pick<WorklistProps, 'state' | 'discrepancies'>) {
  const { t } = useTranslation();

  if (state.failed.length === 0) {
    return null;
  }

  /**
   * A 409 KIJÁRATA MINDKÉT LISTÁT FRISSÍTI — de NEM dobja el a bukást.
   *
   * A jelöltek a HIÁNYLISTÁBÓL jönnek, tehát a munkalista egyedüli
   * újratöltése pont azt hagyná elavultan, ami a 409-et okozta. És mert a
   * hiánylista frissen jön, a helyi lezárás-nyilvántartás (`settled`)
   * elévül: onnantól a szerver mondja meg, mi nyitott.
   *
   * ⚠️ AZ ELDOBÁS KÜLÖN DÖNTÉS, és ez nem pedantéria. Ha a frissítés
   * MAGA IS elbukik — ugyanaz a kiesés, ami a 409-et okozta, tehát épp ez a
   * valószínű eset —, egy itt eldobott bukásról semmi nem maradna: a
   * bizonylat visszakerült a kupacba jelöletlenül, és a könyvelő soha nem
   * tudná meg, hogy vele történt valami. Az „Elvetem" a tudomásulvétel, és
   * azt EMBER mondja ki, nem egy sikertelen hálózati kör.
   */
  const resolveConflict = () => {
    state.clearSettled();
    state.reload();
    discrepancies.reload();
  };

  return (
    // A `role="alert"` A MONDATON VAN, nem az egész sávon. Egy élő régióba
    // csomagolt gomblista minden változásnál újraolvastatná magát a
    // képernyőolvasóval — a `Frissítem`/`Elvetem` gombokat is, minden
    // hibaválasznál. Az `aria-label` viszont nevet ad a `<section>`-nek
    // anélkül, hogy hangosbeszélő lenne belőle.
    <section className="worklist__failures" aria-label={t('worklist.rowMode.bannerLabel')}>
      <p className="worklist__failures-lead" role="alert">
        {t('worklist.rowMode.failedLead', { count: state.failed.length })}{' '}
        <button type="button" onClick={state.focusFailed}>
          {t('worklist.rowMode.jumpToFailed')}
        </button>
      </p>

      <ul>
        {state.failed.map((entry) => {
          // A GOMBOK NEVE A BIZONYLATOT IS VISZI. Nyolc egyidejű bukásnál
          // nyolc azonos nevű „Elvetem" áll a sávban — képernyőolvasóval
          // végigtabolva nem lehet megmondani, MELYIKET veti el. A látható
          // felirat rövid marad; a hozzáférhető név a teljes mondat.
          const name = entry.item.partnerName ?? t('worklist.notYetFiled');

          return (
            <li key={entry.documentId}>
              <span className="worklist__failures-name">{name}</span>
              {/* A KÉT OK KÜLÖN MONDAT. A 409-nél az újrapróbálás ugyanazzal
                  az adattal megint elbukna — a lista elavult, azt kell
                  frissíteni. Egy közös „nem sikerült" a könyvelőt arra
                  tanítaná, hogy nyomja meg még egyszer. */}
              <span className="worklist__failures-reason">
                {entry.reason === 'conflict'
                  ? t('worklist.rowMode.reasonConflict')
                  : t('worklist.rowMode.reasonFailed')}
              </span>
              {entry.reason === 'conflict' ? (
                <button
                  type="button"
                  aria-label={t('worklist.rowMode.refreshFor', { name })}
                  onClick={resolveConflict}
                >
                  {t('worklist.rowMode.refresh')}
                </button>
              ) : (
                <button
                  type="button"
                  aria-label={t('worklist.rowMode.retryFilingFor', { name })}
                  onClick={() => state.retryFailed(entry.documentId)}
                >
                  {t('worklist.rowMode.retryFiling')}
                </button>
              )}
              <button
                type="button"
                aria-label={t('worklist.rowMode.discardFor', { name })}
                onClick={() => state.discardFailed(entry.documentId)}
              >
                {t('worklist.rowMode.discard')}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * A KIZÁRÁS VISSZAVONÁSI SÁVJA — a D9 `X`-e után.
 *
 * NEM HIBA-SÁV, és ezért nem is úgy néz ki. A bukott mentés az
 * `--attention`-t viszi („ide nézz"); ez viszont egy SIKERES művelet
 * nyugtája, csak épp visszavonható. Vörös vagy sárga jelzéssel a könyvelő
 * minden `X` után hibát keresne.
 *
 * A VISSZASZÁMLÁLÓ ITT ÜTEMEZ, nem a horogban. A horog időzítője a
 * LEJÁRATRA szól — egy másodperces körökben futó `setInterval` ott minden
 * ébredésnél újrarenderelné a listát, akkor is, amikor nincs mit tenni. A
 * másodperc-számláló viszont csak ennek a sávnak kell, és csak amíg áll.
 */
function Excluded({ state }: { state: WorklistState }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const waiting = state.pendingExclusions.length;

  useEffect(() => {
    if (waiting === 0) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [waiting]);

  if (waiting === 0) {
    return null;
  }

  return (
    <section className="worklist__excluded" aria-label={t('worklist.exclude.bandLabel')}>
      {/* `<output>`, NEM `<p role="status">`. Ugyanaz az élő régió, de a
          SZEMANTIKUS elem viszi — az `<output>` implicit szerepe `status`.
          És `status`, nem `alert`: az `alert` félbeszakítja a
          képernyőolvasót, ez viszont nyugta, nem baj — udvariasan, a
          gépelés szünetében mondja el. */}
      <output className="worklist__excluded-lead">
        {t('worklist.exclude.lead', { count: waiting })}
      </output>

      <ul>
        {state.pendingExclusions.map((entry) => (
          <UndoRow key={entry.documentId} entry={entry} now={now} state={state} />
        ))}
      </ul>
    </section>
  );
}

function UndoRow({
  entry,
  now,
  state,
}: {
  entry: PendingExclusion;
  now: number;
  state: WorklistState;
}) {
  const { t } = useTranslation();
  const name = entry.item.partnerName ?? t('worklist.notYetFiled');

  return (
    <li>
      <span className="worklist__excluded-name">{name}</span>
      <button
        type="button"
        aria-label={t('worklist.exclude.undoFor', { name })}
        onClick={() => state.undoExclusion(entry.documentId)}
      >
        {/* A HÁTRALÉVŐ IDŐ A GOMBON ÁLL, nem mellette: az a hely, ahova a
            könyvelő néz, amikor meggondolja magát. FÖLFELÉ kerekítve — lefelé
            az utolsó másodpercben „0"-t mutatna, miközben a visszavonás még
            él. */}
        {t('worklist.exclude.undo', { seconds: secondsLeft(entry, now) })}
      </button>
    </li>
  );
}

function QueueRow({
  item,
  needsChoice,
  focused,
  failure,
  state,
}: {
  item: WorklistItem;
  /** A „kétes kötés" jelvény (#54, T19) — a könyvelő a megnyitás előtt lássa. */
  needsChoice: boolean;
  focused: boolean;
  failure: FailedFiling | null;
  state: WorklistState;
}) {
  const { t } = useTranslation();

  // A TÍPUS ÉS A KELTE EGY SORBAN — így áll az artboardon. A dátumból csak a
  // hónap-nap: a kupacban minden sor az idei évből való, és a négy jegy a
  // partner nevétől venné el a helyet. A kelte hiánya nem `—`-lyuk a sor
  // közepén: a típus önmagában is teljes mondat.
  const meta = [
    docTypeLabel(t, item.docType),
    item.issueDate === null ? null : formatMonthDay(item.issueDate),
    item.docTypeCorrected ? t('worklist.typeCorrectedShort') : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <li
      ref={(element) => state.registerRow(item.id, element)}
      // ROVING TABINDEX: egyetlen sor fogadja a `Tab`-ot, a többi közt a
      // nyilak visznek. Enélkül a könyvelőnek annyiszor kellene `Tab`-olnia,
      // ahány bizonylat van.
      tabIndex={focused ? 0 : -1}
      aria-current={focused ? true : undefined}
      // A VISSZAGÖRGETETT SOR MEG VAN JELÖLVE — a sávon kívül a KUPACBAN is,
      // mert a könyvelő ott dolgozik. Enélkül a sor némán visszacsúszna a
      // helyére, és a következő `Enter` úgy menne el mellette, mintha mi sem
      // történt volna.
      className={failure ? 'worklist__row worklist__row--failed' : 'worklist__row'}
      // NINCS `onClick` — a `tabIndex`-szel ellátott sorra kattintva a
      // böngésző FÓKUSZT ad, és az `onFocus` ugyanazt teszi. Egy külön
      // kattintás-kezelő csak az egeret szolgálná ki, a billentyűzetet nem;
      // a hiánylista sorai is így működnek.
      onFocus={() => state.selectDocument(item.id)}
    >
      <span className="row__text">
        <span className="row__partner">
          {item.partnerName ??
            (item.awaitingData ? (
              <span className="muted">{t('worklist.awaitingDataRow')}</span>
            ) : (
              <span className="muted">{t('worklist.notYetFiled')}</span>
            ))}
        </span>
        <span className="row__meta">
          {meta}
          {/* AZ `--attention` SZEREP: „ide nézz, itt dönteni kell". A jelvény a
              meta-sorban áll, nem a név mellett — a név a sor tárgya, és
              272px-en nem osztozhat. (`design/t19-ketes-kotes/`, 1. kép) */}
          {needsChoice ? (
            <>
              {' '}
              <span className="badge badge--attention">{t('worklist.tensoft.badge')}</span>
            </>
          ) : null}
          {/* A „TÍPUS?" JELVÉNY (#93) — ugyanaz a szerep, ugyanaz a hely: a
              könyvelő a bizonylat megnyitása ELŐTT lássa, hol kell dönteni. */}
          {item.typeHint !== null ? (
            <>
              {' '}
              <span className="badge badge--attention">{t('worklist.typeHint.badge')}</span>
            </>
          ) : null}
        </span>
        {failure ? <span className="row__failed">{t('worklist.rowMode.rowFailed')}</span> : null}
      </span>
      <span className="row__amount">
        {item.grossAmount === null
          ? '—'
          : formatAmount(Number(item.grossAmount), { currency: item.currency ?? undefined })}
      </span>
    </li>
  );
}

/**
 * MELYIK BIZONYLATOT DOLGOZOM FEL — a panel fejléce, nem egy „A bizonylat"
 * felirat. A kijelölés a kupacban is látszik, de a döntés ITT születik, és a
 * könyvelőnek nem a szeme sarkából kell ellenőriznie, melyik papírról van szó.
 *
 * A CÉGNÉV MINDIG KIÍRÓDIK, akkor is, amikor a kupac soraiból kimarad: a
 * lefűzés csak a bizonylat SAJÁT cégének hiányát zárhatja le, tehát ez a mező
 * a művelet korlátja, nem dekoráció.
 */
function DocumentHeader({
  item,
  correction,
  onTypeHintDismissed,
}: {
  item: WorklistItem;
  correction: FieldCorrectionState;
  onTypeHintDismissed: () => void;
}) {
  const { t } = useTranslation();

  // A HELYŐRZŐ NEM NÉV, és nem is úgy néz ki: teljes súllyal szedve azt
  // állítaná, hogy „Adatra vár" a partner.
  const title =
    item.partnerName ??
    (item.awaitingData ? t('worklist.awaitingDataRow') : t('worklist.notYetFiled'));
  const titleClass =
    item.partnerName === null ? 'document__partner document__partner--empty' : 'document__partner';

  return (
    <header className="document">
      <h2 className={titleClass}>{title}</h2>
      <p className="document__meta">
        <span>{docTypeLabel(t, item.docType)}</span>
        {item.typeHint !== null ? (
          <span className="badge badge--attention">{t('worklist.typeHint.badge')}</span>
        ) : null}
        {/* A KÖNYVELŐ JAVÍTÁSA ÉS AZ ÜGYFÉL VÁLASZA EGYMÁS MELLETT (#92). A
            javítás nem törli, amit az ügyfél mondott — ha eltér, itt látszik. */}
        {item.docTypeCorrected ? (
          <span className="document__type-corrected">{t('worklist.typeCorrected')}</span>
        ) : null}
        {item.clientDocType !== null && item.clientDocType !== item.docType ? (
          <span className="document__client-type">
            {t('worklist.clientSaidType', { type: docTypeLabel(t, item.clientDocType) })}
          </span>
        ) : null}
        {item.invoiceNumber === null ? null : <span>{item.invoiceNumber}</span>}
        {item.issueDate === null ? null : <span>{formatDate(item.issueDate)}</span>}
      </p>
      <p className="document__company">{item.companyName}</p>
      {item.grossAmount === null ? null : (
        <p className="document__amount">
          {formatAmount(Number(item.grossAmount), { currency: item.currency ?? undefined })}
        </p>
      )}

      {/* A „TÍPUS?" JELZÉS (#93) — a fejlécben, a papír fölött. A javító gombja
          ugyanazt az űrlapot nyitja, mint a „Megváltoztatom", csak a
          kiolvasott típussal előre kiválasztva. */}
      <TypeHintNotice item={item} correction={correction} onDismissed={onTypeHintDismissed} />

      {/* A JAVÍTÁS EGYETLEN BEJÁRATA. A `MunkalistaCBordo` fejlécében ez a
          link már ott állt a terven — most van mögötte művelet.
          ⚠️ NYÍLFÜGGVÉNYBEN: a `start` első paramétere a javasolt típus
          (#93) — közvetlenül átadva a kattintás-eseményt kapná meg. */}
      <p className="document__correct">
        <button type="button" onClick={() => correction.start()} disabled={correction.loading}>
          {t('worklist.correction.open')}
        </button>
      </p>

      {/* A NYITÁS HIBÁJA ITT JELENIK MEG — az űrlap ilyenkor be sem nyílt,
          tehát nincs hova tenni a mezőn belül. */}
      {!correction.open && correction.error !== null ? (
        <p className="document__correct-error" role="alert">
          {correction.error.message}
        </p>
      ) : null}
      {/* AZ EREDETI FÁJL KÖZVETLENÜL ALATTA áll, az „Eredeti" panelen —
          beágyazva, a megnyitó linkjével együtt (#68: a papír a középső
          hasábban). Korábban itt állt egy „Megnyitom" link, és a bizonyíték
          egy másik ablakban volt. */}
    </header>
  );
}

/**
 * A BIZONYLAT — két hasábban: KÖZÉPEN A PAPÍR, JOBBRA A DÖNTÉS (#68, a „B"
 * elrendezés, Viktor 2026-10-19). A 08-31-i `MunkalistaC` („középen amit
 * írsz, jobbra amivel ellenőrzöl") ezt fordítva tartotta; a papír 460 px-en
 * nagyítás nélkül nem volt olvasható, és a jelölők a Tételsoroktól egy
 * hasábnyira estek.
 *
 * KÖZÉPEN csak a bizonylat fejléce és a papír: a papír kapja a rugalmas
 * szélességet. JOBBRA fentről lefelé a döntés sorrendje — melyik hiányt
 * zárja le, a Tensoft-kontír, a Tételsorok —, és ALUL a Lefűzöm, mindig
 * ugyanott: nem mozdul a jelöltek és a sorok számával.
 *
 * EGY RÉGIÓ, KÉT HASÁB. A „A bizonylat" régió a kettőt együtt fogja: a
 * képernyőolvasónak ez egy tárgy, és a fejléctől a Lefűzömig egy úton
 * járható be.
 *
 * AZ „EREDETI" AKKOR IS OTT VAN, ha a fájl még nem jött meg: a panel maga
 * mondja el, mi az állapota. Eltűnő-megjelenő hasáb minden
 * bizonylatváltásnál elrántaná a jobb hasáb helyét, és a könyvelő szeme
 * sor-módban másodpercenként igazodna újra.
 */
function Detail({
  item,
  state,
  discrepancies,
  booking,
  notes,
  fileState,
  tensoft,
}: {
  item: WorklistItem;
  state: WorklistState;
  discrepancies: DiscrepancyListState;
  /**
   * A PUSKA A BIZONYLATHOZ TARTOZIK, tehát a horog a bizonylat
   * azonosítójára jár — bizonylatváltáskor magától újratölt, és a régi
   * papír félbehagyott gépelését még előtte kiüríti. A szülő tartja (#64).
   */
  booking: BookingProposalsState;
  notes: PaperNotesState;
  fileState: DocumentFileState;
  tensoft: TensoftBookingState;
}) {
  const { t } = useTranslation();

  /**
   * A JAVÍTÁS A BIZONYLATHOZ TARTOZIK, ugyanúgy, mint a puska: a horog a
   * bizonylat azonosítójára jár, tehát sorváltáskor magától becsukódik. A
   * mentés UTÁN a listát újratöltjük — a javított mező a soron is látszik,
   * és egy kiürített kötelező mező ki is veheti a sort a lefűzhetők közül
   * (`AWAITING_DATA`).
   */
  // A TÍPUSVÁLASZTÓ CSAK A JAVÍTHATÓ TÍPUSOKON (#92): egy bankkivonatot nem
  // ajánlunk fel átminősítésre — a szerver úgyis `400`-zal utasítaná vissza.
  const correction = useFieldCorrection(
    item.id,
    state.reload,
    CORRECTABLE_DOC_TYPES.includes(item.docType) ? item.docType : null
  );

  /**
   * A JELÖLTEK CSAK A BIZONYLAT SAJÁT CÉGÉBŐL.
   *
   * A szerver ezt őrzi is (cégek közt lefűzve az egyik cég hiánya a másik
   * papírjával záródna le), de a felület nem engedheti odáig: egy másik cég
   * tételét felkínálva a könyvelő azt hinné, hogy jogos választás, és a
   * hibaüzenetből tanulná meg, hogy nem az.
   *
   * A `WAITING` KIMARAD: az türelmi időn belül van, tehát nem bekért hiány.
   * Papírt hozzá is lehet fűzni — de a szelet a BEKÉRT tétel útját
   * bizonyítja, és a türelmi idő alatti lefűzés külön döntés.
   *
   * A MÁR LEZÁRTAK IS KIMARADNAK. A hiánylista a sor-mód alatt NEM töltődik
   * újra (az minden `Enter`-be beépítene egy hálózati kört, épp azt, amiért
   * az optimista mentés van) — tehát a lezárt tételeket a felület tartja
   * nyilván. Enélkül a könyvelő egy perce lefűzött tételt kapna újra
   * jelöltként, és a második `Enter` 409-cel bukna.
   */
  const candidates = (discrepancies.list?.groups ?? [])
    .filter((group) => group.company.id === item.companyId)
    .flatMap((group) => group.items)
    .filter((candidate) => candidate.state !== 'WAITING')
    .filter((candidate) => !state.settledDiscrepancyIds.has(candidate.id));

  const chosen = candidates.find((candidate) => candidate.id === state.candidateId) ?? null;

  // A NYILAK A HASÁBOK KÖZÖTT (#68) — `columnKeys.ts`. A papír jobb szélén a
  // `→` a jelöltekre visz; ha nincs jelölt, a Tételsorokra.
  const region = useRef<HTMLElement>(null);
  const toRight = () => {
    if (!focusCandidates(region.current)) {
      focusNotes(region.current);
    }
  };

  return (
    <section
      ref={region}
      className="worklist__detail"
      aria-label={t('worklist.documentHeading')}
      onKeyDown={(event) => {
        if (columnKey(event, region.current, notes.paper.current)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <div className="worklist__paper">
        {/* ⚠️ A JAVÍTÁS A FEJLÉC HELYÉRE ÜL, nem alá. Ez az „A" irány, és az
            indoka a tételsor: így a lap alsó része NEM mozdul el, a lefűzés
            ott folytatódik, ahol abbahagytad. *(Viktor, 2026-09-15.)* */}
        {correction.open ? (
          <FieldCorrection state={correction} clientDocType={item.clientDocType} />
        ) : (
          <DocumentHeader item={item} correction={correction} onTypeHintDismissed={state.reload} />
        )}
        <DocumentOriginal
          state={fileState}
          notes={notes}
          onExit={state.focusQueue}
          onNext={toRight}
        />
      </div>

      <div className="worklist__side">
        <div className="worklist__side-body">
          {/* A JELÖLTLISTA SAJÁT KOMPONENS, ÉS `key`-elve a bizonylatra (#53).
              A `key` az, ami a kinyitott listát sorváltáskor magától bezárja —
              effekt nélkül, és anélkül, hogy a puska meg a mezőjavítás is
              újraépülne (azok a `Detail`-en maradnak, `key` nélkül). */}
          <Candidates key={item.id} item={item} candidates={candidates} state={state} />

          {chosen ? (
            <>
              <h3>{t('worklist.fieldsHeading')}</h3>
              <dl className="worklist__fields">
                <dt>{t('worklist.fieldInvoiceNumber')}</dt>
                <dd>{chosen.invoiceNumber ?? '—'}</dd>
                <dt>{t('worklist.fieldPartner')}</dt>
                <dd>{chosen.partnerName ?? '—'}</dd>
                <dt>{t('worklist.fieldTaxNumber')}</dt>
                <dd>
                  {chosen.partnerTaxNumber === null
                    ? '—'
                    : formatTaxNumber(chosen.partnerTaxNumber)}
                </dd>
                <dt>{t('worklist.fieldIssueDate')}</dt>
                <dd>{chosen.issueDate === null ? '—' : formatDate(chosen.issueDate)}</dd>
                <dt>{t('worklist.fieldGross')}</dt>
                {/* Forintban — lásd a jelöltek listájánál az indoklást (#39). */}
                <dd>{chosen.amount === null ? '—' : formatAmount(Number(chosen.amount))}</dd>
              </dl>
              <p className="hint">{t('worklist.fieldSource')}</p>
            </>
          ) : null}

          {/* A VALÓDI KONTÍR (`D5`, #54) a párosítás alatt: a könyvelő előbb
              eldönti, melyik hiányt zárja le, és utána nézi meg, mi áll róla
              a Tensoftban. */}
          <TensoftBooking
            state={tensoft}
            documentGross={item.grossAmount}
            documentCurrency={item.currency}
          />

          {/* A KONTÍR-PUSKA. A LEFŰZÉSTŐL FÜGGETLEN: a puska a könyvelő
              jegyzete, és akkor is írható, amikor még nincs kiválasztva
              hiánytétel. A kettőt összekötve a gondolkodás sorrendjét
              írnánk elő — a könyvelő viszont gyakran előbb kontíroz, és csak
              utána dönti el, melyik hiányt zárja le vele. A papír mellett
              áll: a jelölő és a sora így szomszédos hasábban van. */}
          <BookingSheet state={booking} notes={notes} />
        </div>

        {/* A LEFŰZÖM A HASÁB ALJÁN, MINDIG UGYANOTT (#68). Nem görget el a
            jelöltekkel és a sorokkal, és mellette áll, amit lezár — a
            könyvelő az `Enter` előtt egy pillantással látja, mire nyom. */}
        <div className="worklist__file">
          <button
            type="button"
            onClick={state.fileAndAdvance}
            aria-keyshortcuts="Enter"
            // A LEFŰZHETŐSÉGET A SZERVER DÖNTI EL, nem ez a gomb. A mezők a
            // lefűzés pillanatában íródnak rá a bizonylatra, tehát előtte
            // SOHA nincs mezője — egy `readyToFile`-ra kötött tiltás pont
            // azokra a papírokra volna érvényes, amiket le kell fűzni.
            // *(Élesben derült ki, 09-02.)*
            //
            // AZ ELŐELLENŐRZÉS NEM ITT TILT. A hiányos bejelentésű tételt a
            // gomb elfogadja, és az `Enter` MEGMONDJA, mi a baj — a tiltott
            // gomb néma, és a könyvelő nem tudná meg, miért nem megy.
            disabled={!state.candidateId}
          >
            {t('worklist.file')}{' '}
            <span className="worklist__file-key" aria-hidden="true">
              Enter
            </span>
          </button>
          {chosen?.invoiceNumber ? (
            <span className="worklist__closes">
              {t('worklist.closes', { number: chosen.invoiceNumber })}
            </span>
          ) : null}

          {/* AZ ELŐELLENŐRZÉS KIFOGÁSA (2.6/22, 1. pont). Determinisztikus
              visszautasítás: a bejelentésből hiányzik a lefűzéshez kellő
              négy mező egyike, tehát a szerver `DATA_READY` nélkül
              utasítaná vissza. Itt mondjuk meg, a továbblépés ELŐTT — a
              kurzor nem mozdul. */}
          {state.block === 'missingFields' ? (
            <p className="error">{t('worklist.rowMode.missingFields')}</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * A JELÖLTLISTA — rangsorolva, legfeljebb három sorral. (#53, #68)
 *
 * Eddig a cég MINDEN nyitott hiánytétele itt állt, rendezetlenül. A pontozás
 * a `candidateRanking.ts`-ben lakik, mert ott MÉRHETŐ: a komponensbe írva a
 * szabályokat csak felrenderelt DOM sorrendjéből lehetne visszafejteni. Itt
 * annyi dől el, mennyi látszik belőle.
 *
 * ⚠️ A KINYITOTT ÁLLAPOT A `key`-EN MÚLIK. A szülő `key={item.id}`-vel építi,
 * tehát bizonylatváltáskor ez a komponens újra felépül, és a lista magától
 * bezáródik. Effekt nem kell hozzá: egy `useEffect`-es nullázás fölösleges
 * újrarajzolás lenne, és a nyitott lista egy pillanatra átvillanna a
 * következő sorra.
 */
function Candidates({
  item,
  candidates,
  state,
}: {
  item: WorklistItem;
  candidates: DiscrepancyListItem[];
  state: WorklistState;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const ranked = rankCandidates(item, candidates);
  const visible = expanded ? [...ranked.shown, ...ranked.hidden] : ranked.shown;

  return (
    <>
      <h3>{t('worklist.candidatesHeading')}</h3>

      {candidates.length === 0 ? (
        <p className="empty">{t('worklist.candidatesEmpty')}</p>
      ) : (
        <ul className="worklist__candidates">
          {visible.map((candidate) => (
            <li key={candidate.id}>
              <label>
                <input
                  type="radio"
                  name="candidate"
                  checked={candidate.id === state.candidateId}
                  onChange={() => state.setCandidate(candidate)}
                />
                <span>{candidate.partnerName ?? '—'}</span>
                {/* ⚠️ MEGJELÖLÉS, NEM KIVÁLASZTÁS (#53). A rádiógomb üresen
                    marad: egy előre bepipált jelöltet a sor-mód `Enter`-e
                    azonnal lefűzne, és a rossz lefűzés visszafordítása drága.
                    A jelölés a KERESÉST spórolja meg, nem a döntést.
                    *(Viktor, 2026-09-23.)* */}
                {candidate.id === ranked.recommendedId ? (
                  <span className="worklist__candidate-best">{t('worklist.candidateBest')}</span>
                ) : null}
                <span className="muted">
                  {candidate.invoiceNumber ?? '—'}
                  {/* PÉNZFORGALMI (#55) — itt is, a számlaszám mögött, valódi
                      szóközzel (Viktor, 2026-09-29). Csak tájékoztató: a
                      rangsort és a megjelölést nem érinti. */}
                  {candidate.cashAccounting ? (
                    <>
                      {' '}
                      <CashAccountingMark />
                    </>
                  ) : null}
                </span>
                <span className="right">
                  {/* ⚠️ A NAV-TÉTEL ÖSSZEGE FORINT — A `currency` NEM AHHOZ TARTOZIK. (#39)
                      A `NavInvoice.netAmountHUF` + `vatAmountHUF` a NAV forintértéke
                      (`adatmodell.md` §5: „A NAV ad HUF értéket is, tehát az M1-hez nem
                      kell árfolyam-tábla"); a `currency` a számla EREDETI pénzneme. A
                      kettő párosítása egy 55 000 forintos összeget írna ki `55 000 €`-ként.
                      Ezért itt NINCS `currency` — és ez nem felejtés. */}
                  {candidate.amount === null ? '—' : formatAmount(Number(candidate.amount))}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {/* A TÖBBI — gomb csak akkor, ha van is mit mutatni. A szám a gombon
          áll: enélkül a könyvelő nem tudja, egy sort rejtettünk el vagy
          harmincat. */}
      {ranked.hidden.length > 0 ? (
        <p>
          <button
            type="button"
            className="worklist__candidates-more"
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded
              ? t('worklist.candidatesShowLess')
              : t('worklist.candidatesShowAll', { count: ranked.hidden.length })}
          </button>
        </p>
      ) : null}
    </>
  );
}
